/* ──────────────────────────────────────────────────────────────────────────────
   Faz 3 — Araçlar Arası Transfer Detayı  (spec §9)

   Salt-okunur (DetailShell `modeless` + `readOnly`): transfer bir BELGE değil, iki
   terminalin ürettiği OLAY zinciridir — backoffice onu düzenlemez, ÇÖZER.
   Tek yazma yolu: TRANSIT'te asılı farkın çözümü (gerekçe zorunlu).

   Üç bölüm:
     • Satır matrisi — İstenen / Gönderilen / Onaylanan / Fark (+ gerekçeler)
     • Durum zaman çizelgesi — talep → teslim → onay (→ transit çözümü)
     • İki taşıma irsaliyesi — VEREN ve ALAN aracın fişleri; basılmamışsa uyarı.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { unwrapLookupItems } from './c360';
import { DetailShell, Section, Grid } from './detail/DetailShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  ResolveModal, statusLabel, statusTone, requestReasonLabel, shortageReasonLabel, differenceReasonLabel,
  fmtDateTime, fmtQty, fmtDuration, WAYBILL_REQUIRED_STATUSES,
} from './StockTransfersPage';

const TRANSFERS_API = '/api/vehicle-transfers';
const TRANSIT_API = '/api/transit-balances';
const NOTIFY_API = '/api/notifications/broadcast';

const muted = 'var(--text-secondary, #6b7280)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';

const st = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '8px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    color: muted, borderBottom: border,
  },
  td: { padding: '9px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))' },
  num: { textAlign: 'right' },
  danger: { color: 'var(--danger, #e2445c)', fontWeight: 800 },
  ok: { color: 'var(--success, #16a34a)', fontWeight: 700 },
  muted: { color: muted },
  empty: { padding: 12, fontSize: 13, color: muted },
  fact: { display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 14px', background: 'var(--bg-card, #fff)', border, borderRadius: 10 },
  factLabel: { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.2, color: muted },
  factValue: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' },
  tlRow: { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0' },
  tlDot: { width: 12, height: 12, borderRadius: 999, marginTop: 4, flexShrink: 0 },
  tlBody: { display: 'flex', flexDirection: 'column', gap: 2 },
  tlTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary, #0f172a)' },
  tlMeta: { fontSize: 12, color: muted },
  wbRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '12px 14px', border, borderRadius: 10, background: 'var(--bg-card, #fff)',
  },
};

function Fact({ label, value, tone }) {
  return (
    <div style={st.fact}>
      <span style={st.factLabel}>{label}</span>
      <span style={{ ...st.factValue, ...(tone === 'bad' ? st.danger : null) }}>{value ?? '-'}</span>
    </div>
  );
}

export function StockTransferDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = usePageActions();
  usePageMeta('Transfer Detayı', 'Araçlar arası stok transferi — satır matrisi, zaman çizelgesi, taşıma irsaliyeleri.');

  const [header, setHeader] = useState(null);
  const [items, setItems] = useState([]);
  const [waybills, setWaybills] = useState([]);
  const [printed, setPrinted] = useState({ from: false, to: false });
  const [transit, setTransit] = useState([]); // bu transferin TRANSIT'te asılı bakiyesi
  const [warehouses, setWarehouses] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [resolveRow, setResolveRow] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/warehouses/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/representatives/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([w, r]) => {
      setWarehouses(unwrapLookupItems(w));
      setRepresentatives(unwrapLookupItems(r));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const headers = authHeaders();
      const res = await fetch(`${TRANSFERS_API}/transfers/${id}`, { headers });
      if (!res.ok) throw new Error('Transfer alınamadı');
      const body = await res.json();
      const d = body?.data ?? body;
      setHeader(d?.header || null);
      setItems(d?.items || []);
      setWaybills(d?.waybills || []);
      setPrinted({ from: !!d?.fromWaybillPrinted, to: !!d?.toWaybillPrinted });

      // TRANSIT bakiyesi ledger'dan okunur (belgeden türetilmez) → ayrı uç.
      const tRes = await fetch(`${TRANSIT_API}?pendingHours=4`, { headers });
      if (tRes.ok) {
        const tBody = await tRes.json();
        const tData = tBody?.data ?? tBody;
        setTransit((tData?.balances || []).filter((b) => b.transferId === id));
      }
    } catch (e) {
      showToast('error', e.message || 'Transfer alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const vehicleLabel = useCallback((wid) => {
    if (!wid) return '-';
    const w = warehouses.find((x) => x.id === wid);
    return w ? (w.textId || w.name || '-') : '-';
  }, [warehouses]);

  const repName = useCallback((rid) => {
    if (!rid) return '';
    const r = representatives.find((x) => x.id === rid);
    return r?.name || '';
  }, [representatives]);

  const vehicleFull = useCallback((wid, rid) => {
    const plate = vehicleLabel(wid);
    const rep = repName(rid);
    return rep ? `${plate} · ${rep}` : plate;
  }, [vehicleLabel, repName]);

  const totals = useMemo(() => items.reduce((acc, l) => ({
    requested: acc.requested + Number(l.requestedQuantity || 0),
    dispatched: acc.dispatched + Number(l.dispatchedQuantity || 0),
    confirmed: acc.confirmed + Number(l.confirmedQuantity || 0),
  }), { requested: 0, dispatched: 0, confirmed: 0 }), [items]);
  const totalDiff = totals.confirmed - totals.dispatched;

  const waybillOf = (warehouseId) => waybills.find((w) => w.warehouseId === warehouseId);
  const waybillRequired = header && WAYBILL_REQUIRED_STATUSES.has(header.status);
  const waybillMissing = waybillRequired && !(printed.from && printed.to);

  const submitResolve = async ({ action, reason }) => {
    setResolving(true);
    try {
      const res = await fetch(`${TRANSIT_API}/${id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ action, reason }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Fark çözülemedi');
      const d = body?.data ?? body;
      showToast('success', d?.waybillReprintRequired
        ? 'Fark çözüldü — araç stoğu değişti, taşıma irsaliyesi yeniden basılmalı.'
        : 'Fark zayiata yazıldı.');
      setResolveRow(null);
      load();
    } catch (e) {
      showToast('error', e.message || 'Fark çözülemedi');
    } finally {
      setResolving(false);
    }
  };

  /** Basılmamış taşıma irsaliyesi = belgesiz mal taşıyan araç → temsilciye push (§9). */
  const remindWaybill = async () => {
    const targets = [header?.fromRepresentativeId, header?.toRepresentativeId].filter(Boolean);
    if (targets.length === 0) { showToast('error', 'Temsilci bağı yok — hatırlatma gönderilemedi.'); return; }
    setNotifying(true);
    try {
      const res = await fetch(NOTIFY_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          type: 'STOCK_TRANSFER',
          title: 'Taşıma irsaliyesi basılmadı',
          body: `${header?.transferNo || 'Transfer'}: araç stoğunuz değişti. Yola çıkmadan taşıma irsaliyesi basın.`,
          criteria: { representativeIds: targets },
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Hatırlatma gönderilemedi');
      showToast('success', 'Hatırlatma temsilciye gönderildi.');
    } catch (e) {
      showToast('error', e.message || 'Hatırlatma gönderilemedi');
    } finally {
      setNotifying(false);
    }
  };

  const crumb = [
    { label: 'Araç Transferleri', to: '/vehicle-transfers' },
    { label: header?.transferNo || 'Detay', current: true },
  ];

  const actions = (
    <>
      <button type="button" className="dps-btn" onClick={load} disabled={loading}>Yenile</button>
      {canEdit && waybillMissing && (
        <button type="button" className="dps-btn" onClick={remindWaybill} disabled={notifying}>
          {notifying ? 'Gönderiliyor…' : 'İrsaliye Hatırlat'}
        </button>
      )}
      {canEdit && transit.length > 0 && (
        <button type="button" className="dps-btn primary" onClick={() => setResolveRow(transit[0])}>
          Transit Farkını Çöz
        </button>
      )}
      <button type="button" className="dps-btn" onClick={() => navigate('/vehicle-transfers')}>Kapat</button>
    </>
  );

  const statusNode = header ? (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <StatusBadge tone={statusTone(header.status)}>{statusLabel(header.status)}</StatusBadge>
      {header.hasDiscrepancy && <StatusBadge tone="bad">Fark var</StatusBadge>}
      {waybillMissing && <StatusBadge tone="wait">İrsaliyesiz</StatusBadge>}
    </span>
  ) : null;

  // Durum zaman çizelgesi — olayın gerçekten OLDUĞU anlar; boş adım gösterilmez.
  const timeline = [];
  if (header) {
    timeline.push({
      color: 'var(--accent)', title: 'Talep oluşturuldu',
      meta: `${fmtDateTime(header.requestedAt)} · ${vehicleFull(header.toWarehouseId, header.toRepresentativeId)} istedi · Gerekçe: ${requestReasonLabel(header.reason)}`,
    });
    if (header.dispatchedAt) {
      timeline.push({
        color: 'var(--warn)', title: "Teslim edildi — mal TRANSIT'e geçti",
        meta: `${fmtDateTime(header.dispatchedAt)} · ${vehicleFull(header.fromWarehouseId, header.fromRepresentativeId)} verdi · Veren aracın stoğu ${fmtQty(totals.dispatched)} düştü`,
      });
    }
    if (header.status === 'rejected') {
      timeline.push({ color: 'var(--danger)', title: 'Reddedildi', meta: `${fmtDateTime(header.updatedDate)} · ${header.rejectionReason || 'Gerekçe yok'}` });
    }
    if (header.status === 'cancelled') {
      timeline.push({ color: '#94a3b8', title: 'İptal edildi', meta: fmtDateTime(header.updatedDate) });
    }
    if (header.status === 'expired') {
      timeline.push({ color: '#94a3b8', title: 'Süresi doldu (24 saat karşılanmadı)', meta: fmtDateTime(header.updatedDate) });
    }
    if (header.confirmedAt) {
      timeline.push({
        color: header.hasDiscrepancy ? 'var(--warn)' : '#16a34a',
        title: header.hasDiscrepancy ? "Onaylandı — fark TRANSIT'te asılı kaldı" : 'Onaylandı — fark yok',
        meta: `${fmtDateTime(header.confirmedAt)} · Alan aracın stoğu ${fmtQty(totals.confirmed)} arttı${header.hasDiscrepancy ? ` · TRANSIT'te ${fmtQty(Math.abs(totalDiff))} asılı` : ''}`,
      });
    }
    if (header.transitResolvedAt || header.transitResolutionAction) {
      timeline.push({
        color: '#16a34a', title: `Transit farkı çözüldü — ${header.transitResolutionAction}`,
        meta: `${fmtDateTime(header.transitResolvedAt)} · ${header.transitResolutionReason || 'Gerekçe yok'}`,
      });
    }
  }

  return (
    <>
      <DetailShell crumb={crumb} status={statusNode} actions={actions} loading={loading} modeless readOnly
                   audit={header ? { createdDate: header.createdDate, updatedDate: header.updatedDate } : null}>
        {!loading && !header && <Section title="Bulunamadı"><div style={st.empty}>Transfer bulunamadı.</div></Section>}

        {header && (
          <>
            <Section icon="truck" title="Transfer" subtitle="İki araç aynı cariye aittir — bu bir satış değil, depolar arası stok hareketidir (fatura yok).">
              <Grid cols={3}>
                <Fact label="Transfer No" value={header.transferNo} />
                <Fact label="Veren Araç" value={vehicleFull(header.fromWarehouseId, header.fromRepresentativeId)} />
                <Fact label="Alan Araç" value={vehicleFull(header.toWarehouseId, header.toRepresentativeId)} />
                <Fact label="Talep Gerekçesi" value={requestReasonLabel(header.reason)} />
                <Fact label="Talep Zamanı" value={fmtDateTime(header.requestedAt)} />
                <Fact label="Süre" value={fmtDuration(header.requestedAt, header.confirmedAt)} />
                <Fact label="Gönderilen" value={header.dispatchedAt ? fmtQty(totals.dispatched) : '—'} />
                <Fact label="Onaylanan" value={header.confirmedAt ? fmtQty(totals.confirmed) : '—'} />
                <Fact label="Fark" value={header.confirmedAt ? (totalDiff ? `${totalDiff > 0 ? '+' : ''}${fmtQty(totalDiff)}` : '0') : '—'}
                      tone={totalDiff ? 'bad' : undefined} />
                {header.note && <Fact label="Not" value={header.note} />}
                {header.rejectionReason && <Fact label="Red Gerekçesi" value={header.rejectionReason} tone="bad" />}
                {header.transitResolutionAction && <Fact label="Transit Çözümü" value={`${header.transitResolutionAction} — ${header.transitResolutionReason || ''}`} />}
              </Grid>
            </Section>

            {/* TRANSIT'te asılı bakiye — çözüm bekleyen mal. */}
            {transit.length > 0 && (
              <Section icon="box" title="Transit'te Asılı Bakiye"
                       subtitle="Bu kalemler birinin fiziksel olarak elinde ama hiçbir aracın stoğunda görünmüyor."
                       note="Çözüm gerekçe ister ve transferin transit bakiyesinin tamamına uygulanır.">
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={st.th}>Ürün</th>
                      <th style={{ ...st.th, ...st.num }}>Miktar</th>
                      <th style={st.th}>Süre</th>
                      <th style={{ ...st.th, textAlign: 'right' }}>Aksiyon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transit.map((b) => (
                      <tr key={`${b.productId}-${b.stockType}`}>
                        <td style={st.td}>{b.productName || b.productCode || '-'}</td>
                        <td style={{ ...st.td, ...st.num, ...st.danger }}>{fmtQty(b.quantity)}</td>
                        <td style={st.td}>{b.hangingDays != null && b.hangingDays >= 1 ? `${b.hangingDays} gün` : fmtDuration(b.hangingSince)}</td>
                        <td style={{ ...st.td, textAlign: 'right' }}>
                          {canEdit
                            ? <button type="button" className="dps-btn" onClick={() => setResolveRow(b)}>Çöz</button>
                            : <span style={st.muted}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}

            {/* Satır matrisi: İstenen / Gönderilen / Onaylanan / Fark */}
            <Section icon="list" title="Kalemler" subtitle="İstenen → Gönderilen → Onaylanan; fark TRANSIT'te asılı kalır.">
              {items.length === 0 ? (
                <div style={st.empty}>Kalem yok.</div>
              ) : (
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={st.th}>Ürün</th>
                      <th style={st.th}>Birim</th>
                      <th style={st.th}>Parti / SKT</th>
                      <th style={{ ...st.th, ...st.num }}>İstenen</th>
                      <th style={{ ...st.th, ...st.num }}>Gönderilen</th>
                      <th style={{ ...st.th, ...st.num }}>Onaylanan</th>
                      <th style={{ ...st.th, ...st.num }}>Fark</th>
                      <th style={st.th}>Eksik Nedeni</th>
                      <th style={st.th}>Fark Nedeni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((l) => {
                      const diff = Number(l.difference ?? (Number(l.confirmedQuantity || 0) - Number(l.dispatchedQuantity || 0)));
                      return (
                        <tr key={l.id}>
                          <td style={st.td}>{l.productName || l.productCode || '-'}</td>
                          <td style={st.td}>{l.unit || '-'}</td>
                          <td style={st.td}>
                            {l.batchNo || '-'}{l.expiryDate ? ` · ${fmtDateTime(l.expiryDate)}` : ''}
                          </td>
                          <td style={{ ...st.td, ...st.num }}>{fmtQty(l.requestedQuantity)}</td>
                          <td style={{ ...st.td, ...st.num }}>{l.dispatchedQuantity == null ? '—' : fmtQty(l.dispatchedQuantity)}</td>
                          <td style={{ ...st.td, ...st.num }}>{l.confirmedQuantity == null ? '—' : fmtQty(l.confirmedQuantity)}</td>
                          <td style={{ ...st.td, ...st.num, ...(diff ? st.danger : st.muted) }}>
                            {l.confirmedQuantity == null ? '—' : (diff ? `${diff > 0 ? '+' : ''}${fmtQty(diff)}` : '0')}
                          </td>
                          <td style={st.td}>{l.shortageReason ? shortageReasonLabel(l.shortageReason) : '-'}</td>
                          <td style={st.td}>{l.differenceReason ? differenceReasonLabel(l.differenceReason) : '-'}</td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td style={{ ...st.td, fontWeight: 800 }} colSpan={3}>Toplam</td>
                      <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtQty(totals.requested)}</td>
                      <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtQty(totals.dispatched)}</td>
                      <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtQty(totals.confirmed)}</td>
                      <td style={{ ...st.td, ...st.num, ...(totalDiff ? st.danger : st.muted) }}>
                        {totalDiff ? `${totalDiff > 0 ? '+' : ''}${fmtQty(totalDiff)}` : '0'}
                      </td>
                      <td style={st.td} colSpan={2} />
                    </tr>
                  </tbody>
                </table>
              )}
            </Section>

            <Section icon="clock" title="Durum Zaman Çizelgesi">
              {timeline.map((e, i) => (
                <div key={i} style={{ ...st.tlRow, borderTop: i ? border : 'none' }}>
                  <span style={{ ...st.tlDot, background: e.color }} />
                  <span style={st.tlBody}>
                    <span style={st.tlTitle}>{e.title}</span>
                    <span style={st.tlMeta}>{e.meta}</span>
                  </span>
                </div>
              ))}
            </Section>

            {/* İki taşıma irsaliyesi — her iki araçta da stok değişti (§13.10). */}
            <Section icon="hash" title="Taşıma İrsaliyeleri"
                     subtitle="Belge, aracın O ANDAKİ TÜM stoğunu listeler — transfer kalemlerini değil."
                     note={waybillMissing
                       ? 'Basılmamış taşıma irsaliyesi = yolda giden aracın belgesiz mal taşıması. Temsilciye hatırlatma gönderin.'
                       : undefined}>
              {!waybillRequired ? (
                <div style={st.empty}>Bu transferde stok hareketi olmadı — taşıma irsaliyesi gerekmiyor.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { key: 'from', warehouseId: header.fromWarehouseId, repId: header.fromRepresentativeId, title: 'Veren araç fişi' },
                    { key: 'to', warehouseId: header.toWarehouseId, repId: header.toRepresentativeId, title: 'Alan araç fişi' },
                  ].map((side) => {
                    const wb = waybillOf(side.warehouseId);
                    const isPrinted = side.key === 'from' ? printed.from : printed.to;
                    return (
                      <div key={side.key} style={st.wbRow}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 700 }}>
                            {side.title} · {vehicleFull(side.warehouseId, side.repId)}
                          </span>
                          <span style={{ fontSize: 12, color: muted }}>
                            {wb
                              ? `${wb.noteNo || '-'} · ${fmtDateTime(wb.printedAt)} · ${wb.printCount || 0} baskı${wb.isReprint ? ' · yeniden basım' : ''}`
                              : 'Fiş oluşturulmadı'}
                          </span>
                        </div>
                        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                          <StatusBadge tone={isPrinted ? 'ok' : 'bad'}>{isPrinted ? 'Basıldı' : 'Basılmadı'}</StatusBadge>
                          {wb && (
                            <button type="button" className="dps-btn" onClick={() => navigate(`/delivery-notes/${wb.id}`)}>
                              İrsaliyeyi Aç
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>
          </>
        )}
      </DetailShell>

      {resolveRow && (
        <ResolveModal
          row={{ ...resolveRow, transferId: id, fromWarehouseId: header?.fromWarehouseId, toWarehouseId: header?.toWarehouseId, transferNo: header?.transferNo }}
          vehicleLabel={vehicleLabel}
          busy={resolving}
          onClose={() => setResolveRow(null)}
          onSubmit={submitResolve}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

export default StockTransferDetailPage;
